/* eslint-disable dot-notation */

import { Store } from 'vuex';
import { VueRouter } from 'vue-router/types/router.d';
import mean from 'lodash.mean';
import LearningTools from '@/tools/learning';
import BbTools from '@/tools/bluebird';
import RoutePaths from '@/router/route_paths';
import {
  FirebaseLesson,
  Lesson,
  Unit,
  Subscription,
  User,
} from '@/store/types';
import firebase from 'firebase';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import axios, { AxiosResponse } from 'axios';
import DocumentData = firebase.firestore.DocumentData;
import FirebaseUser = firebase.User;

require('firebase/auth');
require('firebase/firestore');

export default {

  getDefaultParams(): URLSearchParams {
    const randomNumber = Math.round(Math.random() * 100000);
    return new URLSearchParams(`device=5&r=${randomNumber}`);
  },

  async loginUser(store: Store<any>, user: FirebaseUser, router: VueRouter): Promise<void> {
    const bbUser: User = {
      uid: user.uid,
      email: user.email!,
      username: '',
      realName: '',
      isAnonymous: user.isAnonymous,
      subscription: null,
      lifetimePremium: false,
      allowFlashSale: false,
      myCourses: [],
      instituteUserPin: '',
      validInstitutePin: false,
      isPersonalizedCourseRestrictedNoSubscription: true,
      phoneticMode: false,
      rapidTest: 0,
    };

    let subscription: Subscription | null = null;
    await Promise.all([
      this.loadUserFromFirestore(store, bbUser),
      this.loadSubscription(store, bbUser),
      this.getFirebaseToken(store),
      this.loadUserLessons(store, bbUser.uid),
      BbTools.loadVideoLanguages(store),
    ]).then((data: any[]): void => {
      let userFirestore;
      [userFirestore, subscription] = data;
      bbUser.subscription = subscription;
      if (userFirestore) {
        if (userFirestore.username && userFirestore.username !== '') {
          bbUser.username = userFirestore.username;
        }
        if (userFirestore.realName && userFirestore.realName !== '') {
          bbUser.realName = userFirestore.realName;
        }
        const pinToCheck = userFirestore.instituteUserPin && userFirestore.instituteUserPin !== ''
          ? userFirestore.instituteUserPin : userFirestore.institutePin;
        bbUser.instituteUserPin = pinToCheck;
        this.checkInstitutePin(store, pinToCheck, true);
        bbUser.lifetimePremium = userFirestore.lifetimePremium;
        bbUser.allowFlashSale = userFirestore.allowFlashSale;
        if (userFirestore.myCourses) {
          bbUser.myCourses = userFirestore.myCourses.split(',');
        }
      }
    });
    store.commit('setUser', bbUser);
    store.commit('setLastVisitedPhraseForEmail', null);

    let newLangI: string | null = null;
    let newLangL: string | null = null;
    const { lessonLast } = store.getters;
    const { lessonLastPersonalized } = store.getters;
    if (lessonLast && lessonLastPersonalized) {
      if (lessonLast.lastAccessSeconds > lessonLastPersonalized.lastAccessSeconds) {
        newLangI = lessonLast.lang_i;
        newLangL = lessonLast.lang_l;
      } else {
        newLangI = lessonLastPersonalized.lang_i;
        newLangL = lessonLastPersonalized.lang_l;
      }
    } else if (lessonLast) {
      newLangI = lessonLast.lang_i;
      newLangL = lessonLast.lang_l;
    } else if (lessonLastPersonalized) {
      newLangI = lessonLastPersonalized.lang_i;
      newLangL = lessonLastPersonalized.lang_l;
    }
    if (this.isOneLanguageSubscription()) {
      if (!this.isLanguageSelectedForOneLanguageSubscription(bbUser)) {
        newLangL = null;
      } else if (bbUser.subscription) {
        newLangL = bbUser.subscription.lang_l;
      }
    }
    if (newLangI) {
      await LearningTools.setNewLangIL(store, newLangI, newLangL);
    } else {
      await LearningTools.setNewLangIL(store, BbTools.DEFAULT_LANG_I.id, null);
    }

    Promise.all([
      BbTools.loadLevelsAllowed(store),
      BbTools.loadPostcardsImages(store),
    ]);

    await router.push(RoutePaths.WELCOME);
  },

  async logoutUser(store: Store<any>, router: VueRouter): Promise<void> {
    store.commit('setUser', null);
    store.commit('setFirebaseToken', '');
    await router.push(RoutePaths.LOGIN);
  },

  async deleteAccount(store: Store<any>, router: VueRouter): Promise<void> {
    const user = firebase.auth().currentUser;
    if (user) {
      try {
        await user.delete();
      } catch (e) {
        if (e && e.message && e.message.indexOf('auth/requires-recent-login') > -1) {
          store.commit('setAlertMessage', { text: 'Delete account error', e });
        }
      }
    }
    this.logoutUser(store, router);
  },

  async getFirebaseToken(store: Store<any>): Promise<void> {
    const result = await firebase.auth().currentUser!.getIdToken(true)
      .then((idToken): string => idToken)
      .catch((error) => {
        store.commit('setAlertMessage', { text: 'Firebase token error #1', error });
        return '';
      });
    store.commit('setFirebaseToken', result);
  },

  async loadUserLessons(store: Store<any>, userUid: string): Promise<void> {
    const lessonsQuery = await firebase.firestore().collection('users')
      .doc(userUid).collection('lessons')
      .get();
    const lessons = new Array<DocumentData>();
    lessonsQuery.forEach((doc) => {
      if (doc.exists) {
        const lesson = doc.data();
        if (lesson.lesson_id) {
          if (lesson.last_access) {
            lesson.lastAccessSeconds = lesson.last_access.seconds;
          }
          lesson.id = doc.id;
          lessons.push(lesson);
        }
      }
    });
    store.commit('setUserLessons', lessons);
  },

  async checkInstitutePin(store: Store<any>, pin: string, saveResult = false): Promise<boolean> {
    let result = false;
    if (pin && pin !== '') {
      const params = BbTools.getDefaultParams();
      params.append('_a', 'checkpin');
      params.append('pin', pin);
      await axios.post(store.getters.URL_INSTITUTION_PIN_CHECK, params, BbTools.bbHeaders)
        .then((response: AxiosResponse) => {
          const responseJson = response.data;
          if (responseJson.success === false) {
            store.commit('setAlertMessage', { text: 'Pin error #1' });
            return;
          }
          result = responseJson.data && responseJson.data.is_auth;
        })
        .catch((error) => {
          store.commit('setAlertMessage', { text: 'Pin error #2', error });
        });
    }
    if (saveResult) {
      store.commit('setValidInstitutePin', result);
      if (pin && pin !== '' && !result) {
        store.commit('setAlertMessage', { text: store.state.tr.InstitutionalNoSubscription });
      }
    }
    return result;
  },

  isOneLanguageSubscription(): boolean {
    return false;
  },

  isLanguageSelectedForOneLanguageSubscription(user: User) {
    return this.isOneLanguageSubscription() && user.subscription
      && user.subscription.lang_l && user.subscription.lang_l !== undefined;
  },

  createBookmark(store: Store<any>, lesson: FirebaseLesson,
    firebaseId: string, imageBackground: string | null): Promise<void> {
    return firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('lessons')
      .doc(firebaseId)
      .set({ ...lesson, isBookmark: true, imageBackground }, { merge: true });
  },

  deleteBookmark(store: Store<any>, lesson: FirebaseLesson): Promise<void> {
    return firebase.firestore().collection('users')
      .doc(store.state.user.uid)
      .collection('lessons')
      .doc(lesson.id)
      .set({ isBookmark: false }, { merge: true });
  },

  /* eslint-disable @typescript-eslint/camelcase */
  async createLessonRecord(store: Store<any>, lessonParamsEncodedWOLangI: string): Promise<void> {
    if (store.state.langI && store.state.langL
      && store.state.level && store.state.unit && store.state.lesson) {
      await firebase.firestore().collection('users')
        .doc(store.state.user.uid).collection('lessons')
        .doc(lessonParamsEncodedWOLangI)
        .set({
          lang_i: store.state.langI.id,
          lang_l: store.state.langL.id,
          level_id: store.state.level.obj_id,
          unit_id: store.state.unit.unit,
          lesson_id: store.state.lesson.id,
          unit_lesson_id: store.state.unit.lesson_id,
          personalized: store.state.personalizedCourse ? store.state.personalizedCourse.id : '',
          personalized_label: store.state.personalizedCourse
            ? store.state.personalizedCourse.CourseName : null,
          completed: false,
          time: 0,
          last_access: firebase.firestore.FieldValue.serverTimestamp(),
          lang_l_label: store.state.langL.text,
          level_label: store.state.level.text,
          unit_label: store.state.unit.unit_text,
          lesson_label: store.state.lesson.text,
          isStored: false,
        });
      await this.loadUserLessons(store, store.state.user.uid);
    }
  },
  /* eslint-disable @typescript-eslint/camelcase */

  /* eslint-disable @typescript-eslint/camelcase */
  async updateLessonRecord(store: Store<any>, lessonParamsEncodedWOLangI: string): Promise<void> {
    if (store.state.langI) {
      await firebase.firestore()
        .collection('users').doc(store.state.user.uid).collection('lessons')
        .doc(lessonParamsEncodedWOLangI)
        .set({
          lang_i: store.state.langI.id, // update interface language
          last_access: firebase.firestore.FieldValue.serverTimestamp(),
          isStored: false,
        }, { merge: true });
      await this.loadUserLessons(store, store.state.user.uid);
    }
  },
  /* eslint-disable @typescript-eslint/camelcase */

  /* eslint-disable @typescript-eslint/camelcase */
  async saveUnitAsStarted(store: Store<any>, langLId: string | null = null,
    levelId: string | null = null, unitId: string | null = null): Promise<void> {
    if (!store.state.unit.started) {
      if (!langLId) {
        langLId = store.state.langL.id;
      }
      if (!levelId) {
        levelId = store.state.level.obj_id;
      }
      if (!unitId) {
        unitId = store.state.unit.unit;
      }
      const unitParamsEncoded = Base64.stringify(Utf8.parse(`llang=${langLId}&unit=${unitId}`));
      await firebase.firestore().collection('users')
        .doc(store.state.user.uid).collection('units')
        .doc(unitParamsEncoded)
        .set({
          lang_l: langLId,
          level_id: levelId,
          unit_id: unitId,
        }, { merge: true });
      await LearningTools.loadUnits(store);
    }
  },
  /* eslint-disable @typescript-eslint/camelcase */

  /* eslint-disable @typescript-eslint/camelcase */
  async saveLevelAsStarted(store: Store<any>, langLId: string | null = null): Promise<void> {
    if (store.state.level && !store.state.level.started) {
      if (!langLId) {
        langLId = store.state.langL.id;
      }
      const levelId = store.state.level.obj_id;
      const levelParamsEncoded = Base64.stringify(Utf8.parse(`llang=${langLId}&level=${levelId}`));
      await firebase.firestore().collection('users')
        .doc(store.state.user.uid).collection('levels')
        .doc(levelParamsEncoded)
        .set({
          lang_l: langLId,
          level_id: levelId,
        }, { merge: true });
      if (store.state.personalizedCourse) {
        await LearningTools.loadLevelsPersonalized(store);
      } else {
        await LearningTools.loadLevels(store);
      }
    }
  },
  /* eslint-disable @typescript-eslint/camelcase */

  async loadUserFromFirestore(store: Store<any>, user: User): Promise<User | null> {
    const doc = await firebase.firestore().collection('users').doc(user.uid).get();
    if (doc && doc.exists) {
      return doc.data() as User;
    }
    return null;
  },

  async loadSubscription(store: Store<any>, user: User) {
    let latestSub: Subscription | null = null;
    const secondsNow = firebase.firestore.Timestamp.now().seconds;
    const subsAndroid = await firebase.firestore()
      .collection('users').doc(user.uid)
      .collection('subscriptions')
      .get(); // for some reason orderBy + limit doesn't work here
    subsAndroid.forEach((doc) => {
      if (doc.exists) {
        const docData = doc.data();
        if (latestSub === null || docData.transactionDate > latestSub.transactionDate) {
          latestSub = {
            lang_l: '',
            expired: docData.expiryTime ? docData.expiryTime.seconds < secondsNow : true,
            transactionDate: docData.transactionDate,
            expiryTime: docData.expiryTime,
          };
        }
      }
    });
    const subsIOS = await firebase.firestore()
      .collection('users').doc(user.uid)
      .collection('subscriptionsIOS')
      .get(); // for some reason orderBy + limit doesn't work here
    subsIOS.forEach((doc) => {
      if (doc.exists) {
        const docData = doc.data();
        if (latestSub === null || docData.transactionDate > latestSub.transactionDate) {
          latestSub = {
            lang_l: '',
            expired: docData.expiryTime ? docData.expiryTime.seconds < secondsNow : true,
            transactionDate: docData.transactionDate,
            expiryTime: docData.expiryTime,
          };
        }
      }
    });
    return latestSub;
  },
  async saveScore(averageScore: number, store: Store<any>) {
    const averageUnitScores = new Array<number>();
    store.state.lessons.forEach((lesson: Lesson) => {
      if (lesson.id === store.state.lesson.id) {
        averageUnitScores.push(averageScore);
      } else if (lesson.score) {
        averageUnitScores.push(lesson.score);
      }
    });
    const firebaseTimeNow = firebase.firestore.FieldValue.serverTimestamp();
    const lessonParamsEncodedWithoutLangI = LearningTools.getFirebaseLessonName(store,
      store.state.langL.id, store.state.lesson.id);
    await firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('lessons')
      .doc(lessonParamsEncodedWithoutLangI)
      .set({ score: averageScore, score_at: firebaseTimeNow }, { merge: true });
    LearningTools.loadLessons(store);
    this.loadUserLessons(store, store.state.user.uid);

    const unitParamsEncoded = Base64.stringify(Utf8
      .parse(`llang=${store.state.langL.id}&unit=${store.state.unit.unit}`));
    const averageUnitScore = Math.round(mean(averageUnitScores));

    await firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('units')
      .doc(unitParamsEncoded)
      .set({
        score: averageUnitScore,
        score_at: firebaseTimeNow,
        lang_l: store.state.langL.id,
        level_id: store.state.level.obj_id,
        unit_id: store.state.unit.unit,
        score_all: averageUnitScores.length >= store.state.lessons.length,
      }, { merge: true });
    LearningTools.loadUnits(store);

    const averageLevelScores = new Array<number>();
    store.state.units.forEach((unit: Unit) => {
      if (unit.unit === store.state.unit.unit) {
        averageLevelScores.push(averageScore);
      } else if (unit.score) {
        averageLevelScores.push(unit.score);
      }
    });
    const levelParamsEncoded = Base64.stringify(Utf8
      .parse(`llang=${store.state.langL.id}&level=${store.state.level.obj_id}`));
    const averageLevelScore = Math.round(mean(averageLevelScores));
    await firebase.firestore().collection('users')
      .doc(store.state.user.uid).collection('levels')
      .doc(levelParamsEncoded)
      .set({
        score: averageLevelScore,
        score_at: firebaseTimeNow,
        score_all: averageLevelScores.length >= store.state.units.length,
      }, { merge: true });
    if (store.state.personalizedCourse) {
      LearningTools.loadLevelsPersonalized(store);
    } else {
      LearningTools.loadLevels(store);
    }
  },
  async togglePhonetic(store: Store<any>) {
    await firebase.firestore().collection('users').doc(store.state.user.uid).set({
      phoneticMode: !store.state.user.phoneticMode,
    }, { merge: true });
    const userFirestore: User | null = await this.loadUserFromFirestore(store, store.state.user);
    if (userFirestore) {
      store.commit('setUser', {
        ...store.state.user,
        phoneticMode: userFirestore.phoneticMode,
      });
    }
  },
  async loadRanks(store: Store<any>, langLessonKeys: Array<string>): Promise<Map<string, number>> {
    const doc = await firebase.firestore().collection('ranked_xps')
      .doc(store.state.user.uid).get();
    const ranks = new Map<string, number>();
    const promises = [];
    for (let i = 0; i < langLessonKeys.length; i += 1) {
      const langLessonKey = `${langLessonKeys[i].split(';')[0]}_${langLessonKeys[i].split(';')[1]}`;
      const scorePromise = doc.ref.collection(langLessonKey).doc('score_details').get();
      promises.push(scorePromise);
    }
    const scores = await Promise.all(promises);
    for (let i = 0; i < langLessonKeys.length; i += 1) {
      const score = scores[i];
      if (score.exists) {
        ranks.set(langLessonKeys[i], score.data()!.rank);
      }
    }
    return ranks;
  },
  async saveRealName(store: Store<any>, realName: string): Promise<void> {
    await firebase.firestore().collection('users').doc(store.state.user.uid).set({
      realName,
    }, { merge: true });
    store.commit('setUser', {
      ...store.state.user,
      realName,
    });
  },
  async saveRapidTestSeen(store: Store<any>): Promise<void> {
    await firebase.firestore().collection('users').doc(store.state.user.uid).set({
      rapidTestSeen: true,
    }, { merge: true });
    store.commit('setUser', {
      ...store.state.user,
      rapidTestSeen: true,
    });
  },
};
